import React from 'react'
import { Navigate, NavLink, Outlet, useLocation, useMatch } from 'react-router-dom';
import { getAuth, createUserWithEmailAndPassword, updateProfile, signOut } from "firebase/auth";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormHelperText,
  Link,
  Stack,
  SvgIcon,
  TextField,
  Typography,
  Container
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Logo } from '../../components/logo';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../hooks/useAuth';

import * as Yup from 'yup';
import { useFormik } from 'formik';
import { addDoc, collection, doc, getFirestore, setDoc, updateDoc } from 'firebase/firestore';

const TOP_NAV_HEIGHT = 64;

const LayoutRoot = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'top center',
  backgroundImage: 'url("/assets/gradient.svg")',

  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  height: '100vh'
}))

export function Register() {

  const initialValues = {
    name: '',
    email: '',
    password: '',
    policy: true,
    submit: null
  }

  const validationSchema = Yup.object({
    name: Yup
      .string()
      .max(255)
      .required('O nome é obrigatório'),
    email: Yup
      .string()
      .email('Deve ser um e-mail válido')
      .max(255)
      .required('O e-mail é obrigatório'),
    password: Yup
      .string()
      .max(255)
      .required('A senha é obrigatória'),
    policy: Yup
      .boolean()
      .oneOf([true], 'Para continuar é necessário aceitar os termos.')
  })

  const formik = useFormik({
  initialValues,
  validationSchema,
  onSubmit: async (values, helpers) => {

  const auth = getAuth();
    createUserWithEmailAndPassword(auth, values.email, values.password)
    .then((userCredential) => {
      // Atualize o displayName do usuário
      const user = userCredential.user;
      
      return updateProfile(user, {
        displayName: values.name,
      })
    })
    .then(() => {
      // Atualização do displayName bem-sucedida
      console.log('displayName atualizado com sucesso');
    })
    .catch((error) => {
      // Trate possíveis erros
      console.log('Erro ao criar usuário:', error);
    })
  }})

  return (
    <LayoutRoot>
      <Box
        component="header"
        sx={{
          left: 0,
          position: 'fixed',
          right: 0,
          top: 40,
          zIndex: (theme) => theme.zIndex.appBar
        }}
      >
        <Container maxWidth="lg">
          <Stack
            spacing={2}
            sx={{ height: TOP_NAV_HEIGHT }}
          >
            <Stack
              alignItems="center"
              direction="row"
              display="inline-flex"
              spacing={1}
              sx={{ textDecoration: 'none' }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  height: 44,
                  width: 44,
                }}
              >
                <Logo />
              </Box>
            </Stack>
          </Stack>
        </Container>
      </Box>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          flex: '1 1 auto'
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            py: {
              xs: '60px',
              md: '120px'
            }
          }}
        >
          <Box sx={{ mb: 4 }}>
          <Link
            color="text.primary"
            sx={{
              alignItems: 'center',
              display: 'inline-flex'
            }}
            underline="hover"
            href="/login"
          >
            <SvgIcon sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </SvgIcon>
            <Typography variant="subtitle2">
              Voltar
            </Typography>
          </Link>
          </Box>

          <Card elevation={16}>
            <CardHeader
              subheader={(
                <Typography
                  color="text.secondary"
                  variant="body2"
                  sx={{
                    mt: 1
                  }}
                >
                  Já tem uma conta?
                  &nbsp;
                  <NavLink to="/login">
                    <Link
                      href="#"
                      underline="hover"
                      variant="subtitle2"
                    >
                      Entrar
                    </Link>
                  </NavLink>
                </Typography>
              )}
              sx={{ pb: 0 }}
              title="Cadastrar-se"
            />
            <CardContent>
              <form
              noValidate
              onSubmit={formik.handleSubmit}
              >
                <Stack spacing={3}>
                  <TextField
                    error={!!(formik.touched.name && formik.errors.name)}
                    fullWidth
                    helperText={formik.touched.name && formik.errors.name}
                    label="Digite seu nome"
                    name="name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.name}
                  />
                  <TextField
                    error={!!(formik.touched.email && formik.errors.email)}
                    fullWidth
                    helperText={formik.touched.email && formik.errors.email}
                    label="Digite seu email"
                    name="email"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type="email"
                    value={formik.values.email}
                  />
                  <TextField
                    error={!!(formik.touched.password && formik.errors.password)}
                    fullWidth
                    helperText={formik.touched.password && formik.errors.password}
                    label="Digite a senha"
                    name="password"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    type="password"
                    value={formik.values.password}
                  />
                </Stack>
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    ml: -1,
                    mt: 1
                  }}
                >
                  <Checkbox
                    checked={formik.values.policy}
                    name="policy"
                    onChange={formik.handleChange}
                  />
                  <Typography
                    color="text.secondary"
                    variant="body2"
                  >
                    Eu li e estou de acordo com
                    {' '}
                    <Link
                      component="a"
                      href="#"
                    >
                      Termos e Condições
                    </Link>
                  </Typography>
                </Box>

                {!!(formik.touched.policy && formik.errors.policy) && (
                <FormHelperText error>
                  {formik.errors.policy}
                </FormHelperText>
              )}
              {formik.errors.submit && (
                <FormHelperText
                  error
                  sx={{ mt: 3 }}
                >
                  {formik.errors.submit}
                </FormHelperText>
              )}
                
                <Button
                  fullWidth
                  size="large"
                  sx={{ mt: 2 }}
                  type="submit"
                  variant="contained"
                >
                  Cadastrar
                </Button>
              </form>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </LayoutRoot>
  )
}
